import { faqs } from "@/data/faq";
import { liveValues, fill } from "@/lib/serviceInfo";

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://bergenkaffeogteknikk.no").replace(/\/$/, "");
const NAME = "Bergen Kaffemaskin- og Teknikk Service";

export const PAGES = {
  "/": {
    title: "Reparasjon av kaffemaskiner i Bergen | Bergen Kaffemaskin- og Teknikk Service",
    description: "Diagnostikk, service og reparasjon av kaffemaskiner og espressomaskiner i Bergen – for private og bedrifter. Jura, DeLonghi, Siemens, La Marzocco og flere. Verksted i Birkeveien 2.",
  },
  "/tjenester": {
    title: "Servicepakker og fra-priser for kaffemaskin | Bergen Kaffemaskin- og Teknikk Service",
    description: "Tre servicepakker for kaffemaskiner: standard diagnostikk, forebyggende service med avkalking og utvidet diagnostikk. Prisoverslag før reparasjon, deler i tillegg.",
  },
  "/innlevering": {
    title: "Lever inn kaffemaskin til reparasjon i Bergen | Digitalt skjema",
    description: "Fyll ut innleveringsskjemaet på under 3 minutter, få ordrenummer med en gang og lever kaffemaskinen på verkstedet i Birkeveien 2, Bergen.",
  },
  "/viktig-info": {
    title: "Viktig informasjon før innlevering | Bergen Kaffemaskin- og Teknikk Service",
    description: "Det du bør vite før du leverer inn kaffemaskinen til service eller reparasjon: diagnostikk først, fra-priser, deler, henting og garanti.",
  },
  "/bedrift": {
    title: "Kaffemaskinservice for kafeer, restauranter og bensinstasjoner i Bergen",
    description: "Service og reparasjon av kaffemaskiner for bedrifter i Bergen og Vestland: kafeer, restauranter, bensinstasjoner, hoteller og kontorer. Send en rask bedriftsforespørsel.",
  },
  "/profesjonelle-kaffemaskiner": {
    title: "Service på profesjonelle espressomaskiner i Bergen | La Marzocco, Rancilio, WMF",
    description: "Service og reparasjon av profesjonelle kaffe- og espressomaskiner for kafeer, restauranter og storkjøkken i Bergen: La Marzocco, Rocket, Rancilio, Nuova Simonelli, Franke, WMF, Eversys.",
  },
  "/transport": {
    title: "Henting og transport av kaffemaskin i Bergen | Bergen Kaffemaskin- og Teknikk Service",
    description: "Kan du ikke levere maskinen selv? Vi kan avtale henting og levering i Bergen og omegn mot tillegg. Transportpris bekreftes før utreise.",
  },
  "/booking": {
    title: "Bestill service og reparasjon av kaffemaskin | Bergen Kaffemaskin- og Teknikk Service",
    description: "Bestill tid for service, reparasjon eller oppmøte for kaffemaskin i Bergen. Velg tidspunkt og beskriv problemet.",
  },
  "/verksted": {
    title: "Verkstedet vårt i Bergen | Bergen Kaffemaskin- og Teknikk Service",
    description: "Verksted for reparasjon og service av kaffemaskiner, symaskiner og storkjøkkenmaskiner i Birkeveien 2, Bergen.",
  },
  "/kontakt": {
    title: "Kontakt og åpningstider – Birkeveien 2, Bergen | Bergen Kaffemaskin- og Teknikk Service",
    description: "Adresse, telefon, åpningstider og veibeskrivelse til kaffemaskinverkstedet i Birkeveien 2, 5093 Bergen. Ring 90 23 22 06.",
  },
  "/faq": {
    title: "Ofte stilte spørsmål om reparasjon av kaffemaskin | Bergen Kaffemaskin- og Teknikk Service",
    description: "Svar om hvilke kaffemaskiner vi reparerer, diagnostikk, priser, garanti, behandlingstid og henting i Bergen.",
  },
  "/vilkar": {
    title: "Vilkår og garantibetingelser | Bergen Kaffemaskin- og Teknikk Service",
    description: "Vilkår for diagnostikk, servicepakker, prisfastsetting, transport, garanti, henting og uavhentet utstyr.",
  },
};

export const PRERENDER_PATHS = Object.keys(PAGES);
const ALIASES = { "/service": "/booking", "/portal": "/sporing" };
const NOINDEX = [/^\/sporing/, /^\/portal/, /^\/godkjenning/, /^\/admin/];

const BRANDS = ["Jura", "Siemens", "Bosch", "DeLonghi", "Miele", "Philips", "Saeco", "Melitta", "Nivona", "Sage", "Breville", "Krups", "La Marzocco", "Rocket", "Rancilio", "Nuova Simonelli", "Franke", "WMF", "Eversys", "La Cimbali"];

function parseHours(range) {
  const m = (range || "").match(/(\d{1,2}:\d{2})\D+(\d{1,2}:\d{2})/);
  return m ? { opens: m[1], closes: m[2] } : null;
}

function business(info) {
  const c = info?.company;
  const ld = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: c?.name || NAME,
    legalName: c?.legal_entity || "Vadims Harjkovs Multihåndverk & El-Tjenester",
    url: `${SITE_URL}/`,
    image: `${SITE_URL}/assets/hero-workshop-repair.png`,
    telephone: `+47 ${c?.phone || "90 23 22 06"}`,
    email: c?.email || "info@bergenkaffeogteknikk.no",
    taxID: c?.org_nr || "935 886 600",
    address: { "@type": "PostalAddress", streetAddress: "Birkeveien 2", postalCode: "5093", addressLocality: "Bergen", addressRegion: "Vestland", addressCountry: "NO" },
    areaServed: [{ "@type": "City", name: "Bergen" }, { "@type": "AdministrativeArea", name: "Vestland" }],
    knowsAbout: ["Reparasjon av kaffemaskiner", "Service av espressomaskiner", "Avkalking", ...BRANDS],
  };
  const h = parseHours(info?.opening_hours?.man_fre);
  if (h) ld.openingHoursSpecification = [{ "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], ...h }];
  return ld;
}

function services(info, audience) {
  return (info?.packages || [{ id: "A", name: "Standard diagnostikk" }, { id: "B", name: "Forebyggende service" }, { id: "C", name: "Utvidet diagnostikk" }]).map((p) => {
    const s = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${p.name} av kaffemaskin`,
      serviceType: "Reparasjon og service av kaffemaskiner",
      provider: { "@id": `${SITE_URL}/#business` },
      areaServed: { "@type": "City", name: "Bergen" },
    };
    if (audience) s.audience = { "@type": "BusinessAudience", name: audience };
    if (p.from_price_nok) s.offers = { "@type": "Offer", priceCurrency: "NOK", priceSpecification: { "@type": "PriceSpecification", minPrice: p.from_price_nok, priceCurrency: "NOK", valueAddedTaxIncluded: true } };
    return s;
  });
}

function faqLd(info) {
  const v = liveValues(info);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: fill(f.a, v) } })),
  };
}

export function headData(rawPath, info) {
  const path = ALIASES[rawPath] || (rawPath.length > 1 ? rawPath.replace(/\/$/, "") : rawPath);
  const page = PAGES[path];
  const noindex = NOINDEX.some((r) => r.test(path));
  const jsonld = [business(info)];
  if (path === "/tjenester") jsonld.push(...services(info));
  if (path === "/bedrift" || path === "/profesjonelle-kaffemaskiner") jsonld.push(...services(info, "Kafeer, restauranter, bensinstasjoner og andre bedrifter"));
  if (path === "/faq") jsonld.push(faqLd(info));
  return {
    title: page?.title || NAME,
    description: page?.description || PAGES["/"].description,
    canonical: `${SITE_URL}${path === "/" ? "/" : path}`,
    robots: noindex ? "noindex,nofollow" : "index,follow",
    jsonld: noindex ? [] : jsonld,
  };
}

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function headHtml(path) {
  const d = headData(path, null);
  return [
    `<title>${esc(d.title)}</title>`,
    `<meta name="description" content="${esc(d.description)}" />`,
    `<meta name="robots" content="${d.robots}" data-seo="robots" />`,
    `<link rel="canonical" href="${esc(d.canonical)}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:locale" content="nb_NO" />`,
    `<meta property="og:site_name" content="${NAME}" />`,
    `<meta property="og:title" content="${esc(d.title)}" />`,
    `<meta property="og:description" content="${esc(d.description)}" />`,
    `<meta property="og:url" content="${esc(d.canonical)}" />`,
    `<meta property="og:image" content="${SITE_URL}/assets/og-share.jpg" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="Reparasjon og service av kaffemaskiner i Bergen og Vestland" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<script type="application/ld+json" id="ld-json">${JSON.stringify(d.jsonld).replace(/</g, "\\u003c")}</script>`,
  ].join("\n        ");
}
